/** Klient for driftsregistrering (/api/drift): timer, vareforbruk, maskinpark og service. Kun eksempeldata. */
import { API_BASE, apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from './api';

const qs = (params) => {
  const u = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== 'alle') u.set(k, v);
  });
  const s = u.toString();
  return s ? `?${s}` : '';
};

/* ---------- Referansedata (medarbeidere, prosjekter, aktiviteter, lønnsarter, tillegg, varer) ----------
   Delt lager for alle registreringssidene. Henter på nytt automatisk ved feil (3 s → 6 s → 12 s → 15 s maks)
   så lenge noen side lytter, og umiddelbart når nettleseren får nett/fokus igjen. */
const refsStore = { data: null, error: null, loading: false, attempts: 0, promise: null, timer: null, listeners: new Set(), version: 0 };

const emitRefs = () => {
  refsStore.version += 1;
  refsStore.listeners.forEach((fn) => fn());
};

function scheduleRefsRetry() {
  if (refsStore.timer || !refsStore.listeners.size) return;
  const delay = Math.min(3000 * 2 ** Math.max(0, refsStore.attempts - 1), 15000);
  refsStore.timer = setTimeout(() => {
    refsStore.timer = null;
    if (refsStore.listeners.size && !refsStore.data) getReferences().catch(() => {});
  }, delay);
}

export function getReferences({ force = false } = {}) {
  if (refsStore.data && !force) return Promise.resolve(refsStore.data);
  // Tvungen henting mens en annen pågår: vent på den, hent så på nytt (så Innstillinger-endringer alltid kommer med).
  if (refsStore.promise) return force ? refsStore.promise.catch(() => null).then(() => getReferences({ force: true })) : refsStore.promise;
  if (refsStore.timer) {
    clearTimeout(refsStore.timer);
    refsStore.timer = null;
  }
  refsStore.loading = true;
  refsStore.error = null;
  emitRefs();
  refsStore.promise = apiGet('/drift/referanser')
    .then((r) => {
      refsStore.data = r;
      refsStore.error = null;
      refsStore.attempts = 0;
      return r;
    })
    .catch((e) => {
      refsStore.error = e;
      refsStore.attempts += 1;
      scheduleRefsRetry();
      throw e;
    })
    .finally(() => {
      refsStore.loading = false;
      refsStore.promise = null;
      emitRefs();
    });
  return refsStore.promise;
}

/** Versjonsnummer for useSyncExternalStore – endres ved hver tilstandsendring. */
export const getReferencesSnapshot = () => refsStore.version;
/** @returns {{ refs: any, error: any, loading: boolean, attempts: number }} */
export const readReferencesState = () => ({ refs: refsStore.data, error: refsStore.error, loading: refsStore.loading, attempts: refsStore.attempts });

const onReconnect = () => {
  if (refsStore.error && !refsStore.data && refsStore.listeners.size) getReferences().catch(() => {});
};

export function subscribeReferences(listener) {
  refsStore.listeners.add(listener);
  if (refsStore.listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('online', onReconnect);
    window.addEventListener('focus', onReconnect);
  }
  if (!refsStore.data && !refsStore.promise) getReferences().catch(() => {});
  return () => {
    refsStore.listeners.delete(listener);
    if (!refsStore.listeners.size && typeof window !== 'undefined') {
      window.removeEventListener('online', onReconnect);
      window.removeEventListener('focus', onReconnect);
      if (refsStore.timer) {
        clearTimeout(refsStore.timer);
        refsStore.timer = null;
      }
    }
  };
}

/** Kun for tester: nullstill lageret. */
export function __resetReferencesForTests() {
  if (refsStore.timer) clearTimeout(refsStore.timer);
  Object.assign(refsStore, { data: null, error: null, loading: false, attempts: 0, promise: null, timer: null, version: 0 });
  refsStore.listeners.clear();
}

/** Etter endringer i Innstillinger: hent referansedata på nytt så skjemaer og filtre viser oppdaterte valg. */
export const invalidateReferences = () => getReferences({ force: true }).catch(() => {});

/* ---------- Aktive valg ----------
   Arkiverte registerposter (active=false) og avsluttede prosjekter/underprosjekter tilbys ikke for nye registreringer,
   men verdien som allerede står på en post beholdes alltid, og etiketten består i lister og rapporter. */
export const isActiveRef = (x) => !!x && x.active !== false && x.status !== 'avsluttet';
export const inactiveLabel = (x) => (x?.status === 'avsluttet' ? 'avsluttet' : 'arkivert');

/**
 * Nedtrekksvalg fra en referanseliste: aktive poster + nåværende verdi. `includeInactive` tar med arkiverte/avsluttede (merket).
 * @param {Array} list  @param {{ value?: string, includeInactive?: boolean, idKey?: string, label?: (x) => string, hint?: (x) => string|undefined }} opts
 */
export function refOptions(list, { value, includeInactive = false, idKey = 'id', label = (x) => x.name ?? x.label ?? x[idKey], hint } = {}) {
  return (list ?? [])
    .filter((x) => includeInactive || isActiveRef(x) || x[idKey] === value)
    .map((x) => {
      const parts = [hint?.(x), !isActiveRef(x) ? inactiveLabel(x) : null].filter(Boolean);
      return { value: x[idKey], label: label(x), hint: parts.length ? parts.join(' · ') : undefined, inactive: !isActiveRef(x) };
    });
}

/** Antall arkiverte/avsluttede poster som ellers er skjult i et nedtrekk (nåværende verdi regnes ikke). */
export const hiddenInactiveCount = (list, value, idKey = 'id') => (list ?? []).filter((x) => !isActiveRef(x) && x[idKey] !== value).length;

/* ---------- Innstillinger (grunnadministrasjon) ----------
   Registrene lagres i backend og speiles inn i de samme referansene som Timer/Vareforbruk/Maskiner/RUH/rapporter bruker. */
export const ADMIN_SECTIONS = [
  { id: 'medarbeidere', label: 'Medarbeidere', registers: ['employees'], description: 'Hvem som kan velges i timer, vareforbruk, saker og avvik. Arkivering tar personen ut av nye valg – historikken består.' },
  { id: 'kunder-prosjekter', label: 'Kunder og prosjekter', registers: ['customers', 'projects'], description: 'Prosjektene registreringene føres på, med kunde, sted, ansvarlig og underprosjekter. Avsluttede prosjekter blir igjen i historikk og rapporter.' },
  { id: 'registreringsoppsett', label: 'Registreringsoppsett', registers: ['activities', 'wageTypes', 'supplements', 'departments', 'groups', 'trades', 'competenceTypes'], description: 'Kategoriene timeføringen bruker. Ingen satser, overtids- eller diettregler – det er ikke konfigurert i demoen.' },
  { id: 'varer-maskiner', label: 'Varer og maskiner', registers: ['itemCategories', 'items', 'machines'], description: 'Varer med fast enhet (enheten låses når varen har forbruk) og maskinregisteret med internnr, type, årsmodell og eksempelplan.' },
  { id: 'firma-rutiner', label: 'Firma og rutiner', registers: ['company'], description: 'Demooppsett, rutinetekster og ærlig status for det som ikke er koblet til.' },
];
export const ADMIN_REGISTER_LABELS = {
  employees: 'Medarbeidere', customers: 'Kunder', projects: 'Prosjekter', activities: 'Aktiviteter', wageTypes: 'Lønnsarter', supplements: 'Mengdetillegg', itemCategories: 'Varekategorier', items: 'Varer',
  departments: 'Avdelinger', groups: 'Grupper', trades: 'Fag', competenceTypes: 'Kompetansetyper', machines: 'Maskiner', company: 'Firma og rutiner',
};
export const adminOverview = () => apiGet('/drift/admin/oversikt');
export const adminListRegister = (name) => apiGet(`/drift/admin/registre/${name}`);
export const adminCreateItem = (name, body) => apiPost(`/drift/admin/registre/${name}`, body).finally(invalidateReferences);
export const adminPatchItem = (name, id, body) => apiPatch(`/drift/admin/registre/${name}/${encodeURIComponent(id)}`, body).finally(invalidateReferences);
export const adminDeleteItem = (name, id) => apiDelete(`/drift/admin/registre/${name}/${encodeURIComponent(id)}`).finally(invalidateReferences);
export const adminAddSubproject = (projectId, body) => apiPost(`/drift/admin/registre/projects/${encodeURIComponent(projectId)}/underprosjekter`, body).finally(invalidateReferences);
export const adminPatchSubproject = (projectId, subId, body) => apiPatch(`/drift/admin/registre/projects/${encodeURIComponent(projectId)}/underprosjekter/${encodeURIComponent(subId)}`, body).finally(invalidateReferences);
export const adminGetCompany = () => apiGet('/drift/admin/firma');
export const adminPatchCompany = (body) => apiPatch('/drift/admin/firma', body).finally(invalidateReferences);
export const adminListMachines = () => apiGet('/drift/admin/maskiner');
export const adminCreateMachine = (body) => apiPost('/drift/admin/maskiner', body).finally(invalidateReferences);
export const adminPatchMachine = (id, body) => apiPatch(`/drift/admin/maskiner/${encodeURIComponent(id)}`, body).finally(invalidateReferences);
export const adminDeleteMachine = (id) => apiDelete(`/drift/admin/maskiner/${encodeURIComponent(id)}`).finally(invalidateReferences);

export const listTimeEntries = (params) => apiGet(`/drift/timer${qs(params)}`);
export const getTimeEntry = (id) => apiGet(`/drift/timer/${id}`);
export const createTimeEntry = (body) => apiPost('/drift/timer', body);
export const updateTimeEntry = (id, body) => apiPut(`/drift/timer/${id}`, body);
export const patchTimeEntry = (id, body) => apiPatch(`/drift/timer/${id}`, body);
export const deleteTimeEntry = (id) => apiDelete(`/drift/timer/${id}`);
/** Send alle utkast for en medarbeider i perioden (Min dag / uke). */
export const sendDraftTimeEntries = (body) => apiPost('/drift/timer/send-utkast', body);

/** Statusflyt for timeføring (forenklet demo – ikke lønns-/godkjenningsregler). Hvem som normalt gjør overgangen: ansatt (Min dag) eller kontor. */
export const TIME_STATUSES = ['utkast', 'sendt', 'returnert', 'godkjent'];
export const TIME_STATUS_FLOW = {
  'utkast>sendt': 'employee',
  'returnert>sendt': 'employee',
  'sendt>utkast': 'employee',
  'sendt>godkjent': 'office',
  'sendt>returnert': 'office',
  'godkjent>sendt': 'office',
};
export const canTransition = (from, to) => Boolean(TIME_STATUS_FLOW[`${from}>${to}`]);

export const listConsumption = (params) => apiGet(`/drift/vareforbruk${qs(params)}`);
export const getConsumption = (id) => apiGet(`/drift/vareforbruk/${id}`);
export const createConsumption = (body) => apiPost('/drift/vareforbruk', body);
export const updateConsumption = (id, body) => apiPut(`/drift/vareforbruk/${id}`, body);
export const deleteConsumption = (id) => apiDelete(`/drift/vareforbruk/${id}`);

export const listMachines = () => apiGet('/drift/maskiner');
export const getMachine = (id, params) => apiGet(`/drift/maskiner/${id}${qs(params)}`);
export const patchMachine = (id, body) => apiPatch(`/drift/maskiner/${id}`, body);
/* ---------- Maskinsaker: feilmeldinger, service og vedlikehold ----------
   Saksgang feil: innmeldt → mottatt → under utbedring → løst. «Løst» frigir aldri maskinen automatisk –
   «kan ikke brukes» krever eksplisitt frigivelse (frigi). Service/vedlikehold: planlagt → utført. */
export const listEvents = (params) => apiGet(`/drift/hendelser${qs(params)}`);
export const getEvent = (id) => apiGet(`/drift/hendelser/${id}`);
export const createEvent = (machineId, body) => apiPost(`/drift/maskiner/${machineId}/hendelser`, body);
export const patchEvent = (id, body) => apiPatch(`/drift/hendelser/${id}`, body);
export const deleteEvent = (id) => apiDelete(`/drift/hendelser/${id}`);
export const addEventNote = (id, body) => apiPost(`/drift/hendelser/${id}/notater`, body);
export const resolveEvent = (id, body) => apiPost(`/drift/hendelser/${id}/los`, body);
export const releaseMachine = (id, body) => apiPost(`/drift/hendelser/${id}/frigi`, body);
export const reopenEvent = (id, body) => apiPost(`/drift/hendelser/${id}/gjenapne`, body ?? {});
export const deleteAttachment = (attachmentId) => apiDelete(`/drift/vedlegg/${attachmentId}`);
export const attachmentUrl = (attachmentId, { download = false } = {}) => `${API_BASE}/drift/vedlegg/${attachmentId}${download ? '?download=1' : ''}`;

/** Vedlegg (bilde/PDF). Regler fra referansedata: maks 4 MB per fil, inntil 6 per sak. */
export const ATTACHMENT_RULES = { maxBytes: 4 * 1024 * 1024, maxPerEvent: 6, allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'], accept: 'image/jpeg,image/png,image/webp,application/pdf', label: 'JPG, PNG, WebP eller PDF · maks 4 MB per fil · inntil 6 vedlegg' };

/** Klientsjekk før opplasting. Returnerer feilmelding eller null. */
export function attachmentProblem(file, existingCount = 0, rules = ATTACHMENT_RULES) {
  if (existingCount >= rules.maxPerEvent) return `Maks ${rules.maxPerEvent} vedlegg per sak`;
  const type = (file.type || '').toLowerCase();
  if (!rules.allowedTypes.includes(type)) return `«${file.name}» (${type || 'ukjent type'}) støttes ikke – bruk JPG, PNG, WebP eller PDF`;
  if (file.size > rules.maxBytes) return `«${file.name}» er ${(file.size / (1024 * 1024)).toFixed(1)} MB – maks 4 MB per fil`;
  if (!file.size) return `«${file.name}» er tom`;
  return null;
}

export function uploadAttachments(eventId, files) {
  const fd = new FormData();
  Array.from(files).forEach((f) => fd.append('files', f, f.name));
  return apiUpload(`/drift/hendelser/${eventId}/vedlegg`, fd);
}

/* ---------- Manuelle avvik: RUH (HMS) og kvalitetsavvik ----------
   Egen statusmodell: innmeldt → under behandling → lukket. Lukking krever en kort konklusjon; gjenåpning bevarer historikken.
   Ikke blandet med maskinsaker eller produksjonsvarsler. Kategorier kan være «Ikke avklart». Kun eksempeldata – ingen melding sendes. */
export const listDeviations = (params) => apiGet(`/drift/avvik${qs(params)}`);
export const getDeviation = (id) => apiGet(`/drift/avvik/${id}`);
export const createDeviation = (body) => apiPost('/drift/avvik', body);
export const patchDeviation = (id, body) => apiPatch(`/drift/avvik/${id}`, body);
export const deleteDeviation = (id) => apiDelete(`/drift/avvik/${id}`);
export const addDeviationComment = (id, body) => apiPost(`/drift/avvik/${id}/kommentarer`, body);
export const addDeviationAction = (id, body) => apiPost(`/drift/avvik/${id}/tiltak`, body);
export const patchDeviationAction = (id, actionId, body) => apiPatch(`/drift/avvik/${id}/tiltak/${actionId}`, body);
export const deleteDeviationAction = (id, actionId) => apiDelete(`/drift/avvik/${id}/tiltak/${actionId}`);
export const closeDeviation = (id, body) => apiPost(`/drift/avvik/${id}/lukk`, body);
export const reopenDeviation = (id, body) => apiPost(`/drift/avvik/${id}/gjenapne`, body ?? {});
export function uploadDeviationAttachments(devId, files) {
  const fd = new FormData();
  Array.from(files).forEach((f) => fd.append('files', f, f.name));
  return apiUpload(`/drift/avvik/${devId}/vedlegg`, fd);
}

export const DEVIATION_KINDS = ['ruh', 'kvalitet'];
export const DEVIATION_KIND_LABELS = { ruh: 'RUH', kvalitet: 'Kvalitetsavvik' };
export const DEVIATION_KIND_LONG = { ruh: 'RUH – HMS-hendelse', kvalitet: 'Kvalitetsavvik' };
export const DEVIATION_STATUSES = ['innmeldt', 'under_behandling', 'lukket'];
export const DEVIATION_STATUS_LABELS = { innmeldt: 'Innmeldt', under_behandling: 'Under behandling', lukket: 'Lukket' };
export const SEVERITIES = ['lav', 'middels', 'hoy', 'kritisk'];
export const SEVERITY_LABELS = { lav: 'Lav', middels: 'Middels', hoy: 'Høy', kritisk: 'Kritisk' };
export const NOT_CLARIFIED = 'ikke_avklart';

/** Rute til saksrommet for et manuelt avvik. */
export const deviationPath = (id) => `/avvik/sak/${encodeURIComponent(id)}`;

export function fileSizeText(bytes) {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ---------- Prosjekter og driftsrapporter ---------- */
export const listProjects = (params) => apiGet(`/drift/prosjekter${qs(params)}`);
export const getProject = (id, params) => apiGet(`/drift/prosjekter/${id}${qs(params)}`);

/** Rapporttyper (samme nøkler som backend). */
export const DRIFT_REPORTS = [
  { id: 'timer', label: 'Timerapport', short: 'Timer', description: 'Arbeidstimer, maskintimer og tillegg – gruppert per prosjekt, medarbeider, dag, aktivitet eller maskin.' },
  { id: 'prosjektforbruk', label: 'Prosjektforbruk', short: 'Prosjektforbruk', description: 'Per prosjekt: timer, maskintimer, tillegg og vareforbruk per vare og enhet.' },
  { id: 'vareforbruk', label: 'Vareforbruk', short: 'Vareforbruk', description: 'Rørdeler, masser og materialer – per vare, prosjekt, kategori eller dag. Enheten følger varen.' },
  { id: 'maskiner', label: 'Maskintimer og service', short: 'Maskiner', description: 'Registrerte maskintimer i perioden, timeteller fra registeret, driftsstatus og servicestatus med åpne saker.' },
  { id: 'hendelser', label: 'Hendelser og service', short: 'Hendelser', description: 'Feilmeldinger, service og vedlikehold – saksgang, ansvar, frister og utført arbeid. Filtrer på maskin, type, status og prioritet.' },
  { id: 'avvik', label: 'HMS, RUH og kvalitetsavvik', short: 'Avvik', description: 'Manuelt meldte RUH og kvalitetsavvik – åpne, forfalte og lukkede saker, kategorier, årsaker og tiltak. Filtrer på type, status, alvorlighet og ansvarlig.' },
];

export const getDriftReport = (type, params) => apiGet(`/drift/rapporter/${type}${qs(params)}`);

/** Nedlastingslenke for eksport av det filtrerte utvalget. fmt: 'csv' | 'xlsx'. detalj=1 gir registreringene (kun CSV). */
export function exportUrl(type, fmt, params, { detail = false } = {}) {
  return `${API_BASE}/drift/eksport/${type}.${fmt}${qs({ ...params, detalj: detail ? 1 : undefined })}`;
}

/** Filternøkler som følger URL-en på driftssidene. Prosjekt og periode er primære, resten ligger under «Flere filtre». */
export const DRIFT_FILTER_LABELS = {
  projectId: 'Prosjekt',
  subprojectId: 'Underprosjekt',
  employeeId: 'Medarbeider',
  machineId: 'Maskin',
  status: 'Status',
  activityId: 'Aktivitet',
  wageTypeId: 'Lønnsart',
  category: 'Kategori',
  itemId: 'Vare',
  type: 'Type',
  eventStatus: 'Sakstatus',
  priority: 'Prioritet',
  assignedToId: 'Tildelt',
  open: 'Utvalg',
  kind: 'Type',
  devStatus: 'Status',
  severity: 'Alvorlighet',
  responsibleId: 'Ansvarlig',
};

/** Lesbar verdi for et aktivt filter – trenger referansedata og (for maskin) maskinlisten. */
export function filterValueLabel(key, value, refs, machines = []) {
  if (!value || value === 'alle') return null;
  switch (key) {
    case 'kind':
      return DEVIATION_KIND_LABELS[value] ?? value;
    case 'devStatus':
      return DEVIATION_STATUS_LABELS[value] ?? value;
    case 'severity':
      return SEVERITY_LABELS[value] ?? value;
    case 'responsibleId':
      return value === 'ingen' ? 'Ikke tildelt' : refs?.employees?.find((e) => e.id === value)?.name ?? value;
    case 'projectId':
      return refs?.projects?.find((p) => p.id === value)?.name ?? value;
    case 'subprojectId':
      return refs?.projects?.flatMap((p) => p.subprojects ?? []).find((s) => s.id === value)?.name ?? value;
    case 'employeeId':
      return refs?.employees?.find((e) => e.id === value)?.name ?? value;
    case 'machineId': {
      if (value === 'med') return 'Kun med maskin';
      if (value === 'uten') return 'Uten maskin';
      const m = machines.find((x) => x.machineId === value);
      return m ? `${m.internalNo} · ${m.name}` : value;
    }
    case 'status':
      return TIME_STATUS_LABELS[value] ?? value;
    case 'activityId':
      return refs?.activities?.find((a) => a.id === value)?.label ?? value;
    case 'wageTypeId':
      return refs?.wageTypes?.find((w) => w.id === value)?.label ?? value;
    case 'category':
      return refs?.categories?.find((c) => c.id === value)?.label ?? value;
    case 'itemId':
      return refs?.items?.find((i) => i.id === value)?.name ?? value;
    case 'type':
      return EVENT_TYPE_LABELS[value] ?? value;
    case 'eventStatus':
      return EVENT_STATUS_LABELS[value] ?? value;
    case 'priority':
      return PRIORITY_LABELS[value] ?? value;
    case 'assignedToId':
      return value === 'ingen' ? 'Ikke tildelt' : refs?.employees?.find((e) => e.id === value)?.name ?? value;
    case 'open':
      return OPEN_FILTER_LABELS[value] ?? value;
    default:
      return value;
  }
}

/** «12,5 tonn · 40 meter · 3 stk» fra {t: 12.5, m: 40, stk: 3}. Aldri summert på tvers av enheter. */
export function byUnitText(byUnit, units) {
  const entries = Object.entries(byUnit ?? {}).filter(([, q]) => q !== null && q !== undefined);
  if (!entries.length) return null;
  const fmt = (q, u) => new Intl.NumberFormat('nb-NO', { maximumFractionDigits: u === 't' ? 1 : u === 'stk' ? 0 : 1 }).format(q);
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([u, q]) => `${fmt(q, u)} ${units?.[u] ?? u}`)
    .join(' · ');
}

/** Perioder for filtrering (Oslo-datoer YYYY-MM-DD). */
export function periodRange(preset, today) {
  const t = new Date(`${today}T12:00:00Z`);
  const iso = (d) => d.toISOString().slice(0, 10);
  const shift = (d, n) => new Date(d.getTime() + n * 86400000);
  const dow = (t.getUTCDay() + 6) % 7; // mandag = 0
  if (preset === 'uke') return { from: iso(shift(t, -dow)), to: iso(shift(t, 6 - dow)) };
  if (preset === 'forrige-uke') return { from: iso(shift(t, -dow - 7)), to: iso(shift(t, -dow - 1)) };
  if (preset === '14') return { from: iso(shift(t, -13)), to: today };
  if (preset === 'maned') return { from: `${today.slice(0, 7)}-01`, to: today };
  return { from: undefined, to: undefined };
}

export const PERIOD_PRESETS = [
  { id: '14', label: 'Siste 14 dager' },
  { id: 'uke', label: 'Denne uken' },
  { id: 'forrige-uke', label: 'Forrige uke' },
  { id: 'maned', label: 'Denne måneden' },
  { id: 'alle', label: 'Hele perioden' },
  { id: 'egen', label: 'Egen periode' },
];

/**
 * Periode fra URL-parametre: forhåndsvalg eller egen fra/til.
 * @param {{ periode?: string | null, from?: string | null, to?: string | null }} [params]
 * @param {string} today
 * @param {string} [defaultPreset]
 */
export function resolvePeriod({ periode, from, to } = {}, today, defaultPreset = '14') {
  // Ukjent forhåndsvalg i URL-en (skrivefeil, gammel lenke) faller tilbake til standard – aldri en tom periodevelger.
  const preset = periode && PERIOD_PRESETS.some((p) => p.id === periode) ? periode : defaultPreset;
  if (preset === 'egen') return { preset, from: from || undefined, to: to || undefined };
  return { preset, ...periodRange(preset, today) };
}

export const TIME_STATUS_LABELS = { utkast: 'Utkast', sendt: 'Sendt', returnert: 'Returnert', godkjent: 'Godkjent' };
/** Kort forklaring per status – vises der den ansatte skal forstå hva som skjer videre. */
export const TIME_STATUS_HELP = {
  utkast: 'Kun du ser utkastet. Send inn når dagen er klar.',
  sendt: 'Sendt til kontoret. Du kan fortsatt rette til den er godkjent.',
  returnert: 'Kontoret har sendt føringen tilbake med en beskjed. Rett og send på nytt.',
  godkjent: 'Godkjent av kontoret og låst. Be kontoret gjenåpne om noe må rettes.',
};
export const EVENT_TYPE_LABELS = { service: 'Service', vedlikehold: 'Vedlikehold', feilmelding: 'Feilmelding' };
export const FAULT_STATUSES = ['innmeldt', 'mottatt', 'under_utbedring', 'lost'];
export const SERVICE_EVENT_STATUSES = ['planlagt', 'utfort'];
export const EVENT_STATUS_LABELS = { innmeldt: 'Innmeldt', mottatt: 'Mottatt', under_utbedring: 'Under utbedring', lost: 'Løst', planlagt: 'Planlagt', utfort: 'Utført' };
export const PRIORITY_LABELS = { lav: 'Lav', normal: 'Normal', hoy: 'Høy', kritisk: 'Kritisk' };
export const PRIORITIES = ['lav', 'normal', 'hoy', 'kritisk'];
export const OPERATIONAL_LABELS = { drift: 'I drift', avklaring: 'Avventer frigivelse', stopp: 'Kan ikke brukes' };
export const OPEN_FILTER_LABELS = { 1: 'Kun åpne saker', 0: 'Kun avsluttede saker' };
export const SERVICE_STATUS_LABELS = { ok: 'OK', snart: 'Service snart', forfalt: 'Service forfalt', ukjent: 'Ukjent' };

/** Neste steg i saksgangen for en feilmelding (uten «løst» – det krever utført arbeid via resolveEvent). */
export function nextFaultStatus(status) {
  if (status === 'innmeldt') return 'mottatt';
  if (status === 'mottatt') return 'under_utbedring';
  return null;
}

/** Lesbar linje for serviceplanen: «6 500 t · 20 t igjen», «Forfalt med 60 t», «Om 12 dager» eller årsak til ukjent. */
export function serviceLine(service, { short = false } = {}) {
  const s = service;
  if (!s) return 'Ukjent';
  if (s.status === 'ukjent') return s.reason ?? 'Ukjent – grunnlag mangler';
  const nf = (v) => new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(v);
  if (s.kind === 'dato') {
    const when = s.nextDueDate ? s.nextDueDate.split('-').reverse().join('.') : '';
    if (s.remaining < 0) return `Forfalt med ${nf(Math.abs(s.remaining))} dager${short ? '' : ` · frist ${when}`}`;
    return `Om ${nf(s.remaining)} dager${short ? '' : ` · frist ${when}`}`;
  }
  const unit = s.unit === 'km' ? 'km' : 't';
  if (s.remaining < 0) return `Forfalt med ${nf(Math.abs(s.remaining))} ${unit}${short ? '' : ` · neste ved ${nf(s.nextDueAt)} ${unit}`}`;
  return `${nf(s.remaining)} ${unit} igjen${short ? '' : ` · neste ved ${nf(s.nextDueAt)} ${unit}`}`;
}

/** Hvorfor en maskin trenger oppfølging – korte, ærlige årsaker i prioritert rekkefølge. */
export function followUpReasons(m) {
  const out = [];
  if (m.operational?.status === 'stopp') out.push({ id: 'stopp', tone: 'red', label: 'Kan ikke brukes' });
  if (m.operational?.status === 'avklaring') out.push({ id: 'avklaring', tone: 'amber', label: 'Avventer frigivelse' });
  if (m.cases?.overdue) out.push({ id: 'frist', tone: 'amber', label: `${m.cases.overdue} ${m.cases.overdue === 1 ? 'forfalt frist' : 'forfalte frister'}` });
  if (m.service?.status === 'forfalt') out.push({ id: 'service-forfalt', tone: 'red', label: 'Service forfalt' });
  if (m.service?.status === 'snart') out.push({ id: 'service-snart', tone: 'amber', label: 'Service snart' });
  if (m.service?.status === 'ukjent') out.push({ id: 'service-ukjent', tone: 'neutral', label: 'Serviceplan ukjent' });
  if (m.cases?.openFaults) out.push({ id: 'feil', tone: 'navy', label: `${m.cases.openFaults} ${m.cases.openFaults === 1 ? 'åpen feil' : 'åpne feil'}` });
  if (m.cases?.openService) out.push({ id: 'service', tone: 'navy', label: `${m.cases.openService} planlagt service` });
  return out;
}

/** Timer som «h:mm»-tekst, f.eks. 7,5 → «7 t 30 min». */
export function hoursText(h) {
  if (h === null || h === undefined) return '–';
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return mm ? `${hh} t ${mm} min` : `${hh} t`;
}
